import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";

/* ═══════════════════════════════════════════════════════
   GAS PIPE SIZING CALCULATOR
   IFGC/NFPA longest-length method for natural gas
   Using simplified Weymouth equation for low-pressure gas
   ═══════════════════════════════════════════════════════ */

interface GasPipeSize {
  nominal: string;
  id: number; // inside diameter inches
}

const GAS_PIPE_SIZES: GasPipeSize[] = [
  { nominal: '3/8"', id: 0.493 },
  { nominal: '1/2"', id: 0.622 },
  { nominal: '3/4"', id: 0.824 },
  { nominal: '1"', id: 1.049 },
  { nominal: '1-1/4"', id: 1.380 },
  { nominal: '1-1/2"', id: 1.610 },
  { nominal: '2"', id: 2.067 },
  { nominal: '2-1/2"', id: 2.469 },
  { nominal: '3"', id: 3.068 },
  { nominal: '3-1/2"', id: 3.548 },
  { nominal: '4"', id: 4.026 },
  { nominal: '5"', id: 5.047 },
  { nominal: '6"', id: 6.065 },
  { nominal: '8"', id: 7.981 },
];

const GAS_PIPE_MATERIALS = [
  { label: "Steel (Sch 40)", factor: 1.0 },
  { label: "CSST (Corrugated SS)", factor: 0.7 },
  { label: "Copper Type L", factor: 0.85 },
  { label: "PE (Polyethylene)", factor: 0.95 },
];

const INLET_PRESSURES = [
  { label: '7" WC (Residential)', wc: 7 },
  { label: '11" WC (Standard)', wc: 11 },
  { label: '14" WC (2 PSI Reg)', wc: 14 },
  { label: '2 PSI (High Pressure)', wc: 55.4 },
  { label: '5 PSI', wc: 138.5 },
];

const PRESSURE_DROPS = [
  { label: '0.5" WC', wc: 0.5 },
  { label: '1.0" WC', wc: 1.0 },
  { label: '3.0" WC (IFGC Standard)', wc: 3.0 },
  { label: '6.0" WC', wc: 6.0 },
];

// BTU ratings for common appliances
const COMMON_APPLIANCES = [
  { name: "Furnace (80k)", btu: 80000 },
  { name: "Furnace (100k)", btu: 100000 },
  { name: "Water Heater (40k)", btu: 40000 },
  { name: "Range/Oven (65k)", btu: 65000 },
  { name: "Dryer (35k)", btu: 35000 },
  { name: "Fireplace (40k)", btu: 40000 },
  { name: "Boiler (150k)", btu: 150000 },
  { name: "Pool Heater (400k)", btu: 400000 },
];

export const GasPipeSizingCalculator = () => {
  const [btuLoad, setBtuLoad] = useState("");
  const [length, setLength] = useState("");
  const [materialIdx, setMaterialIdx] = useState("0");
  const [inletIdx, setInletIdx] = useState("0");
  const [dropIdx, setDropIdx] = useState("2");

  const btu = parseFloat(btuLoad);
  const L = parseFloat(length);
  const material = GAS_PIPE_MATERIALS[parseInt(materialIdx)];
  const inlet = INLET_PRESSURES[parseInt(inletIdx)];
  const drop = PRESSURE_DROPS[parseInt(dropIdx)];
  const hasInput = btu > 0 && L > 0;

  // CFH = BTU / 1000 (natural gas ~1000 BTU/ft³)
  const cfh = btu / 1000;

  const results = useMemo(() => {
    if (!hasInput) return [];

    return GAS_PIPE_SIZES.map((pipe) => {
      // Simplified capacity formula (Spitzglass for low pressure):
      // Q = 2313 × d^(8/3) × √(ΔH / (L × Sg))
      // where ΔH = pressure drop in inches WC, Sg = 0.60 for nat gas
      const Sg = 0.60;
      const capacity = 2313 * Math.pow(pipe.id, 8 / 3) * Math.sqrt(drop.wc / (L * Sg)) * material.factor;

      return {
        nominal: pipe.nominal,
        id: pipe.id,
        capacity: Math.round(capacity),
        capacityBtu: Math.round(capacity * 1000),
        adequate: capacity >= cfh,
      };
    });
  }, [hasInput, cfh, L, drop, material]);

  const recommended = results.find((r) => r.adequate);

  const addAppliance = (appBtu: number) => {
    const current = parseFloat(btuLoad) || 0;
    setBtuLoad(String(current + appBtu));
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Size <span className="text-foreground font-semibold">natural gas piping</span> per IFGC using the longest-length method. Tap appliances below to add their BTU demand.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Total BTU/hr Demand</label>
          <Input type="number" inputMode="decimal" placeholder="Enter total BTU/hr…" value={btuLoad} onChange={(e) => setBtuLoad(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2 block">Quick Add Appliance</label>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_APPLIANCES.map((app) => (
              <button key={app.name} onClick={() => addAppliance(app.btu)}
                className="px-2 py-1 rounded-md bg-secondary text-[10px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors border border-border">
                {app.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Longest Run (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="Length…" value={length} onChange={(e) => setLength(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Material</label>
            <Select value={materialIdx} onValueChange={setMaterialIdx}>
              <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{GAS_PIPE_MATERIALS.map((m, i) => (<SelectItem key={i} value={String(i)}>{m.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Inlet Pressure</label>
            <Select value={inletIdx} onValueChange={setInletIdx}>
              <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{INLET_PRESSURES.map((p, i) => (<SelectItem key={i} value={String(i)}>{p.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pressure Drop</label>
            <Select value={dropIdx} onValueChange={setDropIdx}>
              <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{PRESSURE_DROPS.map((d, i) => (<SelectItem key={i} value={String(i)}>{d.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {hasInput && recommended && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Recommended Size" value={recommended.nominal} unit="" accent />
          <ResultCard label="Pipe Capacity" value={recommended.capacityBtu.toLocaleString()} unit="BTU/hr" />
          <ResultCard label="Demand" value={Math.round(btu).toLocaleString()} unit="BTU/hr" />
          <ResultCard label="Demand (CFH)" value={fmt(cfh, 0)} unit="ft³/hr" />
          <ResultCard label="Utilization" value={fmt((btu / recommended.capacityBtu) * 100, 1)} unit="%" />
        </div>
      )}

      {hasInput && results.length > 0 && (
        <div className="mt-4 animate-fade-in">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Capacity Chart</h3>
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Size</th>
                  <th className="text-right px-4 py-2 text-muted-foreground font-semibold">CFH</th>
                  <th className="text-right px-4 py-2 text-muted-foreground font-semibold">BTU/hr</th>
                  <th className="text-center px-4 py-2 text-muted-foreground font-semibold">OK</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.nominal} className={`border-b border-border/50 ${r.adequate ? "" : "opacity-50"}`}>
                    <td className="px-4 py-2 font-mono-data font-semibold text-foreground">{r.nominal}</td>
                    <td className="px-4 py-2 font-mono-data text-right text-foreground">{r.capacity.toLocaleString()}</td>
                    <td className="px-4 py-2 font-mono-data text-right text-foreground">{r.capacityBtu.toLocaleString()}</td>
                    <td className="px-4 py-2 text-center">{r.adequate ? <span className="text-primary font-bold">✓</span> : <span className="text-destructive">✗</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
